import { Navigate, useLocation } from "react-router-dom";
import { useMemo } from "react";
import { useAuth } from "../hooks/useAuth";

export function shouldForceOnboarding(user) {
  if (!user) return false;
  if (user.isAdmin || user.isCoach || user.isEmployer) return false;

  // Require verified email + profile step + a picked plan before app usage.
  if (user.emailVerified === false) return true;
  if (user.profileComplete === false) return true;    // Gate 2: profile (skip counts)
  if (user.onboardingComplete === false) return true; // Gate 2.5: plan selection

  return false;
}

export default function OnboardingGate() {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Whether the user has actually picked a plan ("really finished signup") now
  // comes straight from user.onboardingComplete (backed by
  // Memberships.IsSelectedByUser) — no separate /membership/me check, which the
  // default Free membership granted at signup used to falsely satisfy.

  const next = useMemo(() => {
    if (!user) return "/login";
    if (user.isCoach) return "/coaching/dashboard";
    if (user.isAdmin) return "/admin/dashboard";
    if (user.isEmployer) return "/employer/dashboard";
    return "/userdashboard";
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-14 w-14 border-b-4 border-primary-600 mx-auto mb-4" />
          <p className="text-slate-600">Preparing onboarding…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Gate 1: email verification (usually enforced by backend login, but keep UI-safe).
  if (user.emailVerified === false) {
    return <Navigate to={`/verify-email?email=${encodeURIComponent(user.email || "")}`} replace />;
  }

  // Gate 2: required profile onboarding (re-uses Signup stage2 UI). A "Skip for
  // now" counts as complete (profileComplete), so this never traps the user on
  // the profile step — they can always get out.
  if (user.profileComplete === false) {
    return <Navigate to="/signup?fromDashboard=1" replace />;
  }

  // Gate 2.5: no plan picked yet (onboardingComplete = false). Sends back through
  // the same route as Gate 2 — Signup.js sees the profile fields are already
  // filled and jumps straight to the membership picker (stage 3).
  if (user.onboardingComplete === false) {
    return <Navigate to="/signup?fromDashboard=1" replace />;
  }

  // Once signup is done, new users go straight into the app — the CSI
  // assessment and onboarding tasks are available from the sidebar/dashboard.
  return <Navigate to={next} replace />;
}
